import { z } from 'zod';
import { BrandedId } from './common.js';

export const ContactMethodKindSchema = z.enum(['phone', 'line', 'email', 'line_user_id']);
export type ContactMethodKindDto = z.infer<typeof ContactMethodKindSchema>;

export const ContactMethodDtoSchema = z
  .object({
    kind: ContactMethodKindSchema,
    value: z.string().min(1).max(200),
    verified: z.boolean().optional(),
  })
  .strict();
export type ContactMethodDto = z.infer<typeof ContactMethodDtoSchema>;

export const CustomerTierSchema = z.enum(['normal', 'vip', 'blacklist']);
export type CustomerTierDto = z.infer<typeof CustomerTierSchema>;
export const CustomerTierReasonSchema = z.enum(['auto', 'manual']);

export const CustomerStatsDtoSchema = z
  .object({
    visitCount: z.number().int().nonnegative(),
    noShowCount: z.number().int().nonnegative(),
    cancellationCount: z.number().int().nonnegative(),
    lastVisitEpoch: z.number().int().nonnegative().nullable(),
    avgPartySize: z.number().nonnegative(),
    totalSpentTwd: z.number().int().nonnegative(),
  })
  .strict();
export type CustomerStatsDto = z.infer<typeof CustomerStatsDtoSchema>;

export const MonthDaySchema = z
  .object({
    month: z.number().int().min(1).max(12),
    day: z.number().int().min(1).max(31),
  })
  .strict();
export type MonthDayDto = z.infer<typeof MonthDaySchema>;

export const CustomerPreferencesDtoSchema = z
  .object({
    birthday: MonthDaySchema.nullable(),
    anniversary: MonthDaySchema.nullable(),
    seatingPreference: z.string().max(100).nullable(),
    dietaryRestrictions: z.array(z.string().min(1).max(40)).max(10),
  })
  .strict();
export type CustomerPreferencesDto = z.infer<typeof CustomerPreferencesDtoSchema>;

export const CustomerDtoSchema = z
  .object({
    customerId: BrandedId,
    venueId: BrandedId,
    displayName: z.string().min(1).max(100),
    contactMethods: z.array(
      z
        .object({
          kind: ContactMethodKindSchema,
          value: z.string().min(1),
          verified: z.boolean(),
        })
        .strict(),
    ),
    tags: z.array(z.string().min(1).max(30)),
    note: z.string().nullable(),
    archivedAt: z.number().int().nonnegative().nullable(),
    stats: CustomerStatsDtoSchema,
    tier: CustomerTierSchema,
    tierReason: CustomerTierReasonSchema,
    preferences: CustomerPreferencesDtoSchema,
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
  })
  .strict();
export type CustomerDto = z.infer<typeof CustomerDtoSchema>;

// PUT /api/staff/customers/:id/preferences
// Partial update semantics: undefined = leave unchanged; null = clear.
export const UpdateCustomerPreferencesBody = z
  .object({
    birthday: MonthDaySchema.nullable().optional(),
    anniversary: MonthDaySchema.nullable().optional(),
    seatingPreference: z.string().max(100).nullable().optional(),
    dietaryRestrictions: z.array(z.string().min(1).max(40)).max(10).optional(),
  })
  .strict();
export type UpdateCustomerPreferencesBody = z.infer<typeof UpdateCustomerPreferencesBody>;

export const UpdateCustomerPreferencesResponse = CustomerPreferencesDtoSchema.extend({
  customerId: BrandedId,
});

// GET /api/staff/customers/upcoming-birthdays
export const UpcomingBirthdayRowSchema = z
  .object({
    customerId: BrandedId,
    displayName: z.string().min(1).max(100),
    birthday: MonthDaySchema,
  })
  .strict();
export type UpcomingBirthdayRowDto = z.infer<typeof UpcomingBirthdayRowSchema>;

export const ListUpcomingBirthdaysResponse = z
  .object({
    items: z.array(UpcomingBirthdayRowSchema),
  })
  .strict();

// POST /api/staff/customers/segments (POST because the filter body can be rich)
export const CustomerSegmentFiltersBody = z
  .object({
    tags: z.array(z.string().min(1).max(30)).optional(),
    tier: z.array(CustomerTierSchema).optional(),
    visitCountMin: z.number().int().nonnegative().optional(),
    visitCountMax: z.number().int().nonnegative().optional(),
    lastVisitWithinDays: z.number().int().nonnegative().max(3650).optional(),
    hasBirthdayWithinDays: z.number().int().nonnegative().max(365).optional(),
  })
  .strict();
export type CustomerSegmentFiltersBody = z.infer<typeof CustomerSegmentFiltersBody>;

export const ListCustomerSegmentsBody = z
  .object({
    venueId: BrandedId,
    filters: CustomerSegmentFiltersBody,
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().nonnegative().default(0),
    includeArchived: z.boolean().default(false),
    /** Today in venue tz, MM-DD format; server falls back to UTC today if absent. */
    today: z
      .string()
      .regex(/^\d{2}-\d{2}$/)
      .optional(),
  })
  .strict();
export type ListCustomerSegmentsBody = z.infer<typeof ListCustomerSegmentsBody>;

export const CustomerSegmentRowSchema = z
  .object({
    customerId: BrandedId,
    displayName: z.string().min(1),
    tags: z.array(z.string()),
    tier: CustomerTierSchema,
    visitCount: z.number().int().nonnegative(),
    lastVisitIso: z.string().datetime().nullable(),
    birthday: MonthDaySchema.nullable(),
    totalSpentTwd: z.number().int().nonnegative(),
  })
  .strict();
export type CustomerSegmentRowDto = z.infer<typeof CustomerSegmentRowSchema>;

export const ListCustomerSegmentsResponse = z
  .object({
    items: z.array(CustomerSegmentRowSchema),
    total: z.number().int().nonnegative(),
  })
  .strict();

// PUT /api/staff/customers/:id/tier
export const SetCustomerTierBody = z
  .union([
    z
      .object({
        mode: z.literal('manual'),
        tier: CustomerTierSchema,
      })
      .strict(),
    z.object({ mode: z.literal('clear') }).strict(),
  ]);
export type SetCustomerTierBody = z.infer<typeof SetCustomerTierBody>;

export const SetCustomerTierResponse = z
  .object({
    customerId: BrandedId,
    tier: CustomerTierSchema,
    tierReason: CustomerTierReasonSchema,
  })
  .strict();

// POST /api/staff/customers
export const UpsertCustomerBody = z
  .object({
    customerId: BrandedId.optional(),
    venueId: BrandedId,
    displayName: z.string().min(1).max(100),
    contactMethods: z.array(ContactMethodDtoSchema).max(10).default([]),
    tags: z.array(z.string().min(1).max(30)).max(20).optional(),
    note: z.string().max(500).nullable().optional(),
  })
  .strict();
export type UpsertCustomerBody = z.infer<typeof UpsertCustomerBody>;

export const UpsertCustomerResponse = z
  .object({
    customerId: BrandedId,
    created: z.boolean(),
  })
  .strict();

// GET /api/staff/customers
export const CustomerListItemDtoSchema = z
  .object({
    customer: CustomerDtoSchema,
    reservationCount: z.number().int().nonnegative(),
    lastVisitIso: z.string().datetime().nullable(),
  })
  .strict();
export type CustomerListItemDto = z.infer<typeof CustomerListItemDtoSchema>;

export const ListCustomersResponse = z
  .object({
    items: z.array(CustomerListItemDtoSchema),
    total: z.number().int().nonnegative(),
  })
  .strict();

// GET /api/staff/customers/:id
export const GetCustomerResponse = CustomerDtoSchema;

// POST /api/staff/customers/:id/archive
export const ArchiveCustomerResponse = z
  .object({
    customerId: BrandedId,
    archivedAt: z.string().datetime(),
  })
  .strict();

// POST /api/staff/customers/:id/merge
export const MergeCustomersBody = z
  .object({
    secondaryId: BrandedId,
  })
  .strict();
export type MergeCustomersBody = z.infer<typeof MergeCustomersBody>;

export const MergeCustomersResponse = z
  .object({
    primaryId: BrandedId,
    secondaryId: BrandedId,
    reservationsMoved: z.number().int().nonnegative(),
    archivedAt: z.string().datetime(),
  })
  .strict();

// GET /api/staff/customers/:id/reservations
export const CustomerReservationDtoSchema = z
  .object({
    reservationId: BrandedId,
    serviceId: BrandedId,
    scenarioType: z.string().min(1),
    partySize: z.number().int().positive(),
    startTimeIso: z.string().datetime(),
    endTimeIso: z.string().datetime(),
    status: z.string().min(1),
    notes: z.string().nullable(),
  })
  .strict();
export type CustomerReservationDto = z.infer<typeof CustomerReservationDtoSchema>;

export const ListCustomerReservationsResponse = z
  .object({
    items: z.array(CustomerReservationDtoSchema),
    total: z.number().int().nonnegative(),
  })
  .strict();
